import { debounce } from "lodash";
import {CSSProperties, PropsWithChildren, useEffect, useLayoutEffect, useRef, useState, DragEvent as ReactDragEvent, RefObject, MouseEventHandler } from "react";
import {boolOr, classNames, hasText} from "utils/helpers";
import "./media.less";

export interface ZoomSize {
  w: number
  h: number
}

interface Props {
  url: string
  imageWidth: number
  imageHeight: number
  maxImageHeight?: number
  maxImageWidth?: number
  className?: string
  alt: string
  modifyHeight?: boolean
  isZoomed: boolean
  enableGlowHover?: boolean
  zoomContainer:  RefObject<HTMLElement>
  toggleZoom? : () => void
  onClick?: MouseEventHandler<HTMLImageElement>
  onImageRectCalculated?: (pos: {x: number, y: number, w: number, h: number}) => void;
}

function styleToPadding(style: CSSStyleDeclaration) {
  const padding = { 
    top: pixelsToNum(style.paddingTop), 
    left: pixelsToNum(style.paddingLeft), 
    right: pixelsToNum(style.paddingRight), 
    bottom: pixelsToNum(style.paddingBottom) 
  };
  return padding;
}

function pixelsToNum(padding: string): number {
  padding = padding.replace(/px$/, '');
  const parsed = Number(padding);
  if (isNaN(parsed))
    return 0;
  else
    return parsed;
}

export default function PresentationImage(props: PropsWithChildren<Props>) {
  const { url, children, toggleZoom, onClick, className, alt, isZoomed, zoomContainer } = props;
  const [originalImageSize, setOriginalImageSize] = useState({
    width: 0, //props.imageWidth,
    height: 0, //props.imageHeight
  });
  let enableGlowHover = true;
  if (typeof props.enableGlowHover === "boolean") {
    enableGlowHover = props.enableGlowHover
  }
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                  
  if (typeof originalImageSize.width === "undefined" || typeof originalImageSize.height === "undefined") {
    console.warn("original image size is undefined");
  }
  
  const modifyHeight = boolOr(props.modifyHeight, true)
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef<HTMLDivElement>(null);
  const [imgRect, setImgRect] = useState({x: 0, y: 0, w: 0, h: 0});
  const [wrapperStyle, setWrapperStyle] = useState<CSSProperties>({});
  const [imageStyle, setImageStyle] = useState<CSSProperties>({});
  const [redrawCount, setRedrawCount] = useState(0);
  
  useLayoutEffect(() => {
    let zoomContainerWidth = 0;
    let zoomContainerHeight = 0;
    
    function adaptSize() {
      if (zoomContainer.current) {
        zoomContainerWidth = zoomContainer.current.clientWidth;
        zoomContainerHeight = zoomContainer.current.clientHeight;

        updateStyles();
      } else {
        zoomContainerWidth = 0;
        zoomContainerHeight = 0;
      }
      
      //console.log("zoom container %dx%d", zoomContainerWidth, zoomContainerHeight)
    }

    adaptSize();
    updateStyles();

    const debouncedAdaptSize = debounce(adaptSize, 100, {leading: false});

    window.addEventListener('resize', debouncedAdaptSize);
    
    function updateStyles() {
      // if (!isZoomed) {
      //   let maxWidth: number|undefined, maxHeight:number|undefined;
      //   if (!!props.maxImageWidth && !!props.maxImageHeight) {
      //     maxWidth = props.maxImageWidth;
      //     maxHeight = props.maxImageHeight;
      //   } else if (!!zoomContainerSize) {
      //     maxWidth = zoomContainerSize.w;
      //     maxHeight = zoomContainerSize.h;
      //   }
      //  
      //   setWrapperStyle({ maxWidth, maxHeight })
      //   setImageStyle({ maxWidth, maxHeight })
      // } else {
      if (originalImageSize.width && originalImageSize.height &&
          imgRef.current && wrapperRef.current && 
          zoomContainerWidth && zoomContainerHeight
          && containerRef.current) {
        const containerStyle = window.getComputedStyle(containerRef.current);
        const padding = styleToPadding(containerStyle);
        const availableWidth = zoomContainerWidth - (padding.left + padding.right);
        const availableHeight = zoomContainerHeight - (padding.top + padding.bottom);
        let scaledImageWidth: number;
        let scaledImageHeight: number;
        if (originalImageSize.width > originalImageSize.height) {
          // 1st pass: calculate best-case scenario
          scaledImageWidth = availableWidth;
          scaledImageHeight = scaledImageWidth * originalImageSize.height / originalImageSize.width;

          // clip by height
          if (scaledImageHeight > availableHeight) {
            scaledImageHeight = availableHeight;
            scaledImageWidth = scaledImageHeight * originalImageSize.width / originalImageSize.height;
          }
        } else {
          // 1st pass
          scaledImageHeight = availableHeight;
          scaledImageWidth = scaledImageHeight * originalImageSize.width / originalImageSize.height;

          // clip by width
          if (scaledImageWidth > availableWidth) {
            scaledImageWidth = availableWidth;
            scaledImageHeight = scaledImageWidth * originalImageSize.height / originalImageSize.width;
          }
        }

        const marginLeft = (availableWidth - scaledImageWidth) / 2;
        const marginTop = 
          modifyHeight 
            ? (availableHeight - scaledImageHeight) / 2
            : 0;

        // console.log("available (%dx%d) image (%dx%d) pos=(%dx%d)",
        //   availableWidth, availableHeight,
        //   scaledImageWidth, scaledImageHeight,
        //   marginLeft, marginTop);

        setWrapperStyle({
          width: scaledImageWidth,
          height: scaledImageHeight,
          marginLeft: marginLeft,
          marginTop: marginTop
        })
        setImageStyle({
          width: scaledImageWidth,
          height: scaledImageHeight,
        })
        setImgRect({
          x: marginLeft,
          y: marginTop,
          w: scaledImageWidth,
          h: scaledImageHeight
        })
      } else {
        //console.warn("Can't calculate zoomed image size!");
      }
      // }
    }

    return function() {
      window.removeEventListener('resize', debouncedAdaptSize);
    }
  }, [
    zoomContainer, zoomContainer?.current,
    url, 
    isZoomed, 
    originalImageSize.width, originalImageSize.height,
    redrawCount,
    modifyHeight
  ])
  
  
  useLayoutEffect(() => {
    if ((zoomContainer.current == null 
      || zoomContainer.current.clientWidth == 0 
      || zoomContainer.current.clientHeight == 0)) {
      setTimeout(() => {
        // console.log("forcing redraw!");
        setRedrawCount(prev => prev + 1)
      })
    }
  }, [zoomContainer.current, zoomContainer.current?.clientWidth, zoomContainer.current?.clientHeight])

  useEffect(() => {
    if (props.onImageRectCalculated) {
      // console.log('onImageRectCalculated')
      props.onImageRectCalculated(imgRect);
    }
  }, [imgRect, imgRect.x, imgRect.y, imgRect.w, imgRect.h]);
  
  function handleImageLoaded() {
    if (imgRef.current) {
      // console.log('img loaded')
      setOriginalImageSize({
        width: imgRef.current.naturalWidth,
        height: imgRef.current.naturalHeight,
      })
    }
    
    
    //console.log('imageLoaded')
    //setRedrawCount(prev => prev + 1)
  }
  
  function handleDragStart(e: ReactDragEvent<HTMLImageElement>) {
    e.preventDefault()
  }

  if (!hasText(url) || !(zoomContainer?.current)) {
    // console.log('No image URL or no zoom container')
    return null;
  }

  return (
    <div 
      className={classNames("media image", className, {
        "glow-on-hover": enableGlowHover  
      })}
      draggable={false}
      ref={containerRef}
    >
      <div className="wrapper" ref={wrapperRef} style={wrapperStyle} draggable={false}>
        {typeof toggleZoom === "function" 
          ? <>
              <img 
                className={classNames({
                  "zoomed": isZoomed
                })}
                alt={alt} src={url}
                onClick={toggleZoom} ref={imgRef}
                draggable={false} 
                style={imageStyle}
                onLoad={handleImageLoaded}
                onDragStart={handleDragStart}
              />
              <button className="zoom" onClick={toggleZoom} draggable={false} ref={zoomRef as any} />
            </>
          : <img 
            alt={alt} src={url}
            onClick={onClick} ref={imgRef}
            draggable={false}
            onDragStart={handleDragStart}
            onLoad={handleImageLoaded}
            style={imageStyle}
          />
        }
        {children}
      </div>
    </div>
  )
}